import { joiResolver } from "@hookform/resolvers/joi";
import { signInWithRedirect } from "aws-amplify/auth";
import { RhfInput } from "components/rhfs/RhfInput";
import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import type { SignUpInput } from "hooks/authProvider/authProvider";
import Joi from "joi";
import type React from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";

type SignUpInputUIProps = {
  onSignUp: SubmitHandler<SignUpInput>;
  defaultEmail?: string;
};

export const SignUpInputUI: React.VFC<SignUpInputUIProps> = ({
  onSignUp,
  defaultEmail,
}) => {
  const schema = Joi.object({
    familyName: Joi.string().required().messages({
      "string.empty": "姓は必須項目です",
    }),
    givenName: Joi.string().required().messages({
      "string.empty": "名は必須項目です",
    }),
    familyNameKana: Joi.string().allow(""),
    givenNameKana: Joi.string().allow(""),
    email: Joi.string().required().messages({
      "string.empty": "メールアドレスは必須項目です",
    }),
    password: Joi.string().required().messages({
      "string.empty": "パスワードは必須項目です",
    }),
  });
  const methods = useForm<SignUpInput>({
    resolver: joiResolver(schema),
    defaultValues: {
      email: defaultEmail || "",
    },
  });

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSignUp)} className="space-y-4">
            {/* 氏名 */}
            <div>
              <label className="label">
                <span className="label-text font-medium">氏名</span>
                <span className="label-text-alt text-error">必須</span>
              </label>
              <div className="flex gap-3">
                <RhfInput
                  name="familyName"
                  placeholder="姓"
                  data-testid="sign-up-input-family-name"
                />
                <RhfInput
                  name="givenName"
                  placeholder="名"
                  data-testid="sign-up-input-given-name"
                />
              </div>
            </div>

            {/* ふりがな */}
            <div>
              <label className="label">
                <span className="label-text font-medium">ふりがな</span>
                <span className="label-text-alt text-base-content/50">
                  任意
                </span>
              </label>
              <div className="flex gap-3">
                <RhfInput
                  name="familyNameKana"
                  placeholder="せい"
                  data-testid="sign-up-input-family-name-kana"
                />
                <RhfInput
                  name="givenNameKana"
                  placeholder="めい"
                  data-testid="sign-up-input-given-name-kana"
                />
              </div>
            </div>

            {/* メールアドレス */}
            <div>
              <label className="label">
                <span className="label-text font-medium">メールアドレス</span>
                <span className="label-text-alt text-error">必須</span>
              </label>
              <RhfInput
                name="email"
                type="email"
                placeholder="example@company.com"
                data-testid="sign-up-input-username"
              />
            </div>

            {/* パスワード */}
            <div>
              <label className="label">
                <span className="label-text font-medium">パスワード</span>
                <span className="label-text-alt text-error">必須</span>
              </label>
              <RhfInput
                name="password"
                type="password"
                placeholder="パスワードを入力"
                data-testid="sign-up-input-password"
              />
              <label className="label">
                <span className="label-text-alt text-base-content/50">
                  大文字、小文字、数字、記号を含む8文字以上
                </span>
              </label>
            </div>

            {/* 登録ボタン */}
            <div className="pt-4">
              <Button
                type="submit"
                width="full"
                data-testid="sign-up-input-signup"
              >
                アカウントを作成
              </Button>
            </div>
          </form>
        </FormProvider>

        {/* Google登録 */}
        <div className="divider text-base-content/50 text-sm">または</div>
        {/* Google 公式ブランディングの白ボタンは Button リーフの variant に
            乗らないため、ここだけ生 className を許容する */}
        <button
          type="button"
          // eslint-disable-next-line no-restricted-syntax
          className="btn w-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium text-sm gap-3"
          onClick={() => signInWithRedirect({ provider: "Google" })}
          data-testid="sign-up-input-google"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            className="w-5 h-5"
          >
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
          </svg>
          Google で登録
        </button>

        {/* ログインリンク */}
        <div className="divider text-base-content/50 text-sm" />
        <p className="text-center text-sm">
          すでにアカウントをお持ちですか？{" "}
          <Link to="/login" data-testid="sign-up-input-to-signin">
            ログイン
          </Link>
        </p>
      </div>
    </div>
  );
};
